import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  TextField,
  Typography,
  Avatar,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material'
import type { SvgIconProps } from '@mui/material/SvgIcon'
import type { ComponentType } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { ref } from 'firebase/database'
import { useObject } from 'react-firebase-hooks/database'
import { updateProfile } from 'firebase/auth'
import { useAuth } from 'src/contexts/AuthContext'
import { signUserOut, changePasswordWithCurrent, resetPassword } from 'src/utils/auth'
import { update } from '@batical/common'
import { database } from 'src/shims/firebase'
import LogoutIcon from '@mui/icons-material/Logout'
import GoogleIcon from '@mui/icons-material/Google'
import AppleIcon from '@mui/icons-material/Apple'
import FacebookIcon from '@mui/icons-material/Facebook'
import MicrosoftIcon from '@mui/icons-material/Microsoft'
import GitHubIcon from '@mui/icons-material/GitHub'
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import MailOutlineIcon from '@mui/icons-material/MailOutline'

type RtdbProfile = {
  firstname?: string
  lastname?: string
  company?: string
  displayName?: string
  email?: string
  id?: string
  createdAt?: number
  lastModified?: number
}

const PROVIDER_ICONS: Record<string, { label: string; Icon: ComponentType<SvgIconProps> }> = {
  'google.com': { label: 'Google', Icon: GoogleIcon },
  'apple.com': { label: 'Apple', Icon: AppleIcon },
  'facebook.com': { label: 'Facebook', Icon: FacebookIcon },
  'microsoft.com': { label: 'Microsoft', Icon: MicrosoftIcon },
  'github.com': { label: 'GitHub', Icon: GitHubIcon },
  'twitter.com': { label: 'Twitter / X', Icon: PhoneIphoneIcon }
}

function providerChip(providerId: string) {
  const meta = PROVIDER_ICONS[providerId] ?? {
    label: providerId.replace('.com', ''),
    Icon: LockOutlinedIcon
  }
  const Icon = meta.Icon
  return (
    <Chip
      key={providerId}
      icon={<Icon sx={{ '&&': { fontSize: 18 } }} />}
      label={meta.label}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  )
}

export function ProfilePage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const theme = useTheme()
  const uid = user?.uid ?? ''

  const [snap, rtdbLoading] = useObject(uid ? ref(database, `users/${uid}/profile`) : null)

  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [company, setCompany] = useState('')

  const [currentPwd, setCurrentPwd] = useState('')
  const [newPwd, setNewPwd] = useState('')
  const [confirmPwd, setConfirmPwd] = useState('')

  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveMessage, setSaveMessage] = useState<string>()
  const [pwdStatus, setPwdStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [pwdMessage, setPwdMessage] = useState<string>()
  const [resetPwdStatus, setResetPwdStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [resetPwdMessage, setResetPwdMessage] = useState<string>()
  const [showCurrentPwd, setShowCurrentPwd] = useState(false)
  const [showNewPwd, setShowNewPwd] = useState(false)
  const [showConfirmPwd, setShowConfirmPwd] = useState(false)
  const [profileFormInit, setProfileFormInit] = useState(false)

  const hasPasswordProvider = useMemo(
    () => user?.providerData?.some((p) => p.providerId === 'password') ?? false,
    [user]
  )

  /** Fournisseurs OAuth uniquement (pas le provider « password »). */
  const oauthProviders = useMemo(
    () => user?.providerData?.filter((p) => p.providerId !== 'password') ?? [],
    [user]
  )

  /** Uniquement comptes « e-mail + mot de passe » — pas si Google / Apple / etc. sont liés. */
  const showPasswordSection = hasPasswordProvider && oauthProviders.length === 0

  useEffect(() => {
    setProfileFormInit(false)
  }, [uid])

  useEffect(() => {
    if (!uid || profileFormInit) return
    if (rtdbLoading) return
    if (snap && snap.val() != null) {
      const v = snap.val() as RtdbProfile
      setFirstname((v.firstname ?? '').trim() || splitDisplayName(user?.displayName).first)
      setLastname((v.lastname ?? '').trim() || splitDisplayName(user?.displayName).last)
      setCompany((v.company ?? '').trim())
      setProfileFormInit(true)
      return
    }
    const { first, last } = splitDisplayName(user?.displayName)
    setFirstname(first)
    setLastname(last)
    setCompany('')
    setProfileFormInit(true)
  }, [uid, snap, rtdbLoading, profileFormInit, user?.displayName])

  const handleSaveProfile = useCallback(async () => {
    if (!user || !uid) return
    setSaveStatus('saving')
    setSaveMessage(undefined)
    try {
      const fn = firstname.trim()
      const ln = lastname.trim()
      const co = company.trim()
      const displayName = [fn, ln].filter(Boolean).join(' ') || user.displayName || ''

      await update(`users/${uid}/profile`, {
        firstname: fn,
        lastname: ln,
        company: co,
        displayName,
        email: user.email ?? '',
        id: uid,
        lastModified: Math.floor(Date.now() / 1000)
      })
      await updateProfile(user, { displayName })
      setSaveStatus('saved')
      setSaveMessage('Informations enregistrées (Realtime Database + profil Firebase).')
      setTimeout(() => setSaveStatus('idle'), 4000)
    } catch (e: unknown) {
      console.error(e)
      setSaveStatus('error')
      setSaveMessage(e instanceof Error ? e.message : 'Erreur lors de l’enregistrement.')
    }
  }, [user, uid, firstname, lastname, company])

  const handleChangePassword = useCallback(async () => {
    if (!showPasswordSection) return
    if (newPwd.length < 6) {
      setPwdStatus('error')
      setPwdMessage('Le nouveau mot de passe doit faire au moins 6 caractères.')
      return
    }
    if (newPwd !== confirmPwd) {
      setPwdStatus('error')
      setPwdMessage('Les mots de passe ne correspondent pas.')
      return
    }
    setPwdStatus('saving')
    setPwdMessage(undefined)
    try {
      await changePasswordWithCurrent(currentPwd, newPwd)
      setCurrentPwd('')
      setNewPwd('')
      setConfirmPwd('')
      setPwdStatus('saved')
      setPwdMessage('Mot de passe mis à jour.')
      setTimeout(() => setPwdStatus('idle'), 4000)
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code
      let msg = 'Impossible de changer le mot de passe.'
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        msg = 'Mot de passe actuel incorrect.'
      } else if (code === 'auth/weak-password') {
        msg = 'Mot de passe trop faible.'
      } else if (code === 'auth/requires-recent-login') {
        msg = 'Veuillez vous déconnecter puis vous reconnecter avant de changer le mot de passe.'
      } else if (e instanceof Error) {
        msg = e.message
      }
      setPwdStatus('error')
      setPwdMessage(msg)
    }
  }, [showPasswordSection, currentPwd, newPwd, confirmPwd])

  const handleSendPasswordResetEmail = useCallback(async () => {
    const em = user?.email
    if (!em) {
      setResetPwdStatus('error')
      setResetPwdMessage('Aucun e-mail sur ce compte.')
      return
    }
    setResetPwdStatus('saving')
    setResetPwdMessage(undefined)
    try {
      await resetPassword(em)
      setResetPwdStatus('saved')
      setResetPwdMessage(
        'Si cet e-mail est connu, Firebase a envoyé un lien. Vérifiez la boîte de réception et les courriers indésirables.'
      )
      setTimeout(() => setResetPwdStatus('idle'), 6000)
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code
      let msg = 'Impossible d’envoyer l’e-mail.'
      if (code === 'auth/too-many-requests') msg = 'Trop de demandes. Réessayez plus tard.'
      else if (e instanceof Error) msg = e.message
      setResetPwdStatus('error')
      setResetPwdMessage(msg)
    }
  }, [user?.email])

  const handleLogout = useCallback(async () => {
    await signUserOut()
    navigate('/')
  }, [navigate])

  const email = user?.email ?? '—'

  return (
    <>
      <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
        Mon profil
      </Typography>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
        Logo et coordonnées pour les PDF / e-mails :{' '}
        <RouterLink to="/mail-profile" style={{ fontWeight: 600, color: '#4277b9' }}>
          configurer l’en-tête documents
        </RouterLink>
        .
      </Typography>

      <Paper
        sx={{
          maxWidth: 560,
          borderRadius: 3,
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}>
        <Box
          sx={{
            p: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2.5,
            background: 'linear-gradient(135deg, rgba(66,119,185,0.06) 0%, rgba(53,216,156,0.04) 100%)'
          }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              fontSize: '1.4rem',
              fontWeight: 700,
              bgcolor: theme.palette.primary.main
            }}>
            {(firstname?.[0] ?? lastname?.[0] ?? user?.email?.[0] ?? 'U').toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2c3e50' }}>
              {[firstname, lastname].filter(Boolean).join(' ') || user?.displayName || 'Utilisateur'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#7f8c8d', wordBreak: 'break-all' }}>
              {email}
            </Typography>
            {import.meta.env.DEV && uid && (
              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                  fontFamily: 'ui-monospace, monospace',
                  wordBreak: 'break-all',
                  display: 'block',
                  mt: 0.5
                }}>
                uid: {uid}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
            Compte et connexion
          </Typography>
          <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 1.5 }}>
            Email d’inscription (identifiant Firebase)
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>
            {email}
          </Typography>

          {oauthProviders.length > 0 && (
            <>
              <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 1 }}>
                Connexion (réseaux sociaux / partenaires)
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {oauthProviders.map((p) => (
                  <span key={p.providerId}>{providerChip(p.providerId)}</span>
                ))}
              </Box>
            </>
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 2 }}>
            Informations personnelles
          </Typography>
          {rtdbLoading && (
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Chargement du profil…
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Prénom"
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              fullWidth
              size="small"
              autoComplete="given-name"
            />
            <TextField
              label="Nom"
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              fullWidth
              size="small"
              autoComplete="family-name"
            />
            <TextField
              label="Nom d’entreprise"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              fullWidth
              size="small"
              autoComplete="organization"
            />
          </Box>
          {saveMessage && (
            <Alert severity={saveStatus === 'error' ? 'error' : 'success'} sx={{ mt: 2 }}>
              {saveMessage}
            </Alert>
          )}
          <Button
            variant="contained"
            onClick={handleSaveProfile}
            disabled={saveStatus === 'saving' || !uid}
            sx={{ mt: 2, borderRadius: 2, fontWeight: 600, textTransform: 'none', px: 3 }}
          >
            {saveStatus === 'saving' ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </Box>

        {showPasswordSection && (
          <>
            <Divider />
            <Box sx={{ p: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
                Sécurité — mot de passe
              </Typography>
              <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 2 }}>
                Réservé aux comptes créés avec e-mail et mot de passe. Minimum 6 caractères. Si vous avez oublié le
                mot de passe, utilisez le lien par e-mail ci-dessous.
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 400 }}>
                    <TextField
                      label="Mot de passe actuel"
                      type={showCurrentPwd ? 'text' : 'password'}
                      value={currentPwd}
                      onChange={(e) => setCurrentPwd(e.target.value)}
                      size="small"
                      autoComplete="current-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label="Afficher le mot de passe actuel"
                              onClick={() => setShowCurrentPwd((v) => !v)}>
                              {showCurrentPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      label="Nouveau mot de passe"
                      type={showNewPwd ? 'text' : 'password'}
                      value={newPwd}
                      onChange={(e) => setNewPwd(e.target.value)}
                      size="small"
                      autoComplete="new-password"
                      helperText="Minimum 6 caractères (exigence Firebase)."
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label="Afficher le nouveau mot de passe"
                              onClick={() => setShowNewPwd((v) => !v)}>
                              {showNewPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <TextField
                      label="Confirmer le nouveau mot de passe"
                      type={showConfirmPwd ? 'text' : 'password'}
                      value={confirmPwd}
                      onChange={(e) => setConfirmPwd(e.target.value)}
                      size="small"
                      autoComplete="new-password"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              edge="end"
                              size="small"
                              aria-label="Afficher la confirmation"
                              onClick={() => setShowConfirmPwd((v) => !v)}>
                              {showConfirmPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Box>
                  {pwdMessage != null && pwdMessage !== '' && (
                    <Alert severity={pwdStatus === 'error' ? 'error' : 'success'} sx={{ mt: 2, maxWidth: 480 }}>
                      {pwdMessage}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleChangePassword}
                    disabled={
                      pwdStatus === 'saving' || !currentPwd || !newPwd || !confirmPwd || newPwd.length < 6
                    }
                    sx={{
                      mt: 2,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 3,
                      backgroundColor: '#4277b9',
                      '&:hover': { backgroundColor: '#3666a3' }
                    }}>
                    {pwdStatus === 'saving' ? 'Mise à jour…' : 'Enregistrer le nouveau mot de passe'}
                  </Button>

              {user?.email && (
                <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
                    Mot de passe oublié
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f8c8d', mb: 2 }}>
                    Un lien sera envoyé à <strong>{user.email}</strong> pour en choisir un nouveau.
                  </Typography>
                  {resetPwdMessage != null && resetPwdMessage !== '' && (
                    <Alert severity={resetPwdStatus === 'error' ? 'error' : 'success'} sx={{ mb: 2, maxWidth: 480 }}>
                      {resetPwdMessage}
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    startIcon={<MailOutlineIcon />}
                    onClick={handleSendPasswordResetEmail}
                    disabled={resetPwdStatus === 'saving'}
                    sx={{ borderRadius: 2, fontWeight: 600, textTransform: 'none' }}>
                    {resetPwdStatus === 'saving' ? 'Envoi…' : 'Recevoir le lien par e-mail'}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}

        <Divider />

        <Box sx={{ p: 3 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              borderWidth: 2,
              '&:hover': { borderWidth: 2, backgroundColor: 'rgba(244,67,54,0.04)' }
            }}>
            Se déconnecter
          </Button>
        </Box>
      </Paper>
    </>
  )
}

function splitDisplayName(displayName: string | null | undefined): { first: string; last: string } {
  if (!displayName?.trim()) return { first: '', last: '' }
  const parts = displayName.trim().split(/\s+/)
  if (parts.length === 1) return { first: parts[0], last: '' }
  return { first: parts[0], last: parts.slice(1).join(' ') }
}
